import {
  getIdentity,
  getServiceApplicationsInServiceCollection,
  getServiceCollectionSubscriptionStatusForIdentity,
} from "../../actions/api";
import { getMyRequests } from "../../actions";
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import ServiceApplicationsListItem from "./ServiceApplicationsListItem";
import {
  Badge,
  Button,
  Checkbox,
  List,
  Space,
  Input,
  Tooltip,
  Switch,
} from "antd";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  DatabaseOutlined,
  AppstoreOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { PageHeader2, ServiceHeader } from "../headers/PageHeaders";
import ManagedResourceTypesList from "../managedResources/ManagedResourceTypesList";
import { ErrorBar } from "@authzsvc/common-react-components";
import { useParams } from "react-router";
import "../listcard.css";

const { Search } = Input;

const ServiceApplicationsList = ({
  getServiceApplicationsInServiceCollection,
  getServiceCollectionSubscriptionStatusForIdentity,
  serviceCollectionEligibilityStatus,
  serviceCollection,
  serviceApplications,
  isLoading,
  getIdentity,
  identity,
}) => {
  const dispatch = useDispatch();
  const [serviceApplicationTags, setServiceApplicationTags] = useState([]);
  const [checkedList, setCheckedList] = useState([]);
  const { serviceCollectionId } = useParams();
  const [filter, setFilter] = useState([]);
  const [filterStatusEnabled, setFilterStatusEnabled] = useState(false);

  useEffect(() => {
    if (!identity) getIdentity();
  }, [getIdentity, identity]);

  useEffect(() => {
    if (identity)
      getServiceCollectionSubscriptionStatusForIdentity(
        serviceCollectionId,
        identity.id,
      );
  }, [
    getServiceCollectionSubscriptionStatusForIdentity,
    serviceCollectionId,
    identity,
  ]);

  useEffect(() => {
    getServiceApplicationsInServiceCollection(serviceCollectionId);
  }, [getServiceApplicationsInServiceCollection, serviceCollectionId]);

  useEffect(() => {
    if (serviceApplications) {
      let tmpArray = [];
      for (const serviceApplication of serviceApplications) {
        if (serviceApplication.tags) {
          tmpArray = [...serviceApplication.tags, ...tmpArray];
        }
      }
      setServiceApplicationTags([...new Set(tmpArray)].sort());
    }
  }, [serviceApplications]);

  useEffect(() => {
    setCheckedList(serviceApplicationTags);
  }, [serviceApplicationTags]);

  const handleRefresh = () => {
    dispatch(getMyRequests());
    getServiceApplicationsInServiceCollection(serviceCollectionId);
  };

  const checkAll = serviceApplicationTags.length === checkedList.length;
  const indeterminate =
    checkedList.length > 0 &&
    checkedList.length < serviceApplicationTags.length;
  const onChange = (list) => {
    setCheckedList(list);
  };
  const onCheckAllChange = (e) => {
    setCheckedList(e.target.checked ? serviceApplicationTags : []);
  };

  const search = async (value) => {
    setFilter(value ? value.toLowerCase() : value);
  };

  const toggle = () => {
    setFilterStatusEnabled(!filterStatusEnabled);
  };

  if (
    serviceCollectionEligibilityStatus &&
    serviceCollectionEligibilityStatus !== "Subscribed"
  ) {
    return (
      <ErrorBar
        errors={[
          {
            title: "Access Denied.",
            data: {
              message:
                "You are not eligible or subscribed to this Service Collection. Please contact Service Desk.",
            },
          },
        ]}
        dismissError={() => {
          window.location.href = "/";
        }}
      />
    );
  } else
    return (
      <div>
        <ServiceHeader
          title={serviceCollection?.displayName}
          subTitle={serviceCollection?.description}
          loading={isLoading}
          button={
            serviceCollection?.homePage ? (
              <Tooltip title={serviceCollection.homePage}>
                <Button
                  color="default"
                  variant="text"
                  style={{ color: "white" }}
                  href={serviceCollection?.homePage}
                  target="_blank"
                  icon={<QuestionCircleOutlined />}
                />
              </Tooltip>
            ) : (
              <></>
            )
          }
        />

        <PageHeader2
          title="Managed Applications"
          subTitle="Applications are (web) application access rights, service access rights, licences, etc."
          icon={<AppstoreOutlined />}
          search={
            <>
              {serviceApplicationTags.length > 0 && (
                <>
                  Filters: <Switch size="small" onChange={toggle} />
                </>
              )}
              <Search
                placeholder="Search Applications"
                allowClear
                onChange={(e) => search(e.target.value)}
                onPressEnter={(e) => search(e.target.value)}
                onClear={() => search()}
                loading={isLoading}
                style={{ marginBottom: "5px" }}
                disabled={!(checkAll || serviceApplicationTags.length <= 0)}
              />
            </>
          }
        />

        {filterStatusEnabled && serviceApplicationTags.length > 0 && (
          <Space style={{ marginBottom: "10px" }}>
            Filter:
            <Checkbox
              indeterminate={indeterminate}
              onChange={onCheckAllChange}
              checked={checkAll}
            >
              All
            </Checkbox>
            <Checkbox.Group
              options={serviceApplicationTags}
              value={checkedList}
              onChange={onChange}
            />
          </Space>
        )}

        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 3,
          }}
          loading={isLoading}
          dataSource={
            checkAll || serviceApplicationTags.length <= 0
              ? filter
                ? serviceApplications.filter(
                    (application) =>
                      application.displayName.toLowerCase().includes(filter) ||
                      (application.description
                        ? application.description.toLowerCase().includes(filter)
                        : false),
                  )
                : serviceApplications
              : serviceApplications.filter((x) =>
                  x.tags.some((tag) => checkedList.includes(tag)),
                )
          }
          rowKey={(item) => item.id}
          renderItem={(item) => (
            <List.Item>
              {item.tags && item.tags.length > 0 ? (
                <Badge.Ribbon text={item.tags.join(", ")} color="cyan">
                  <ServiceApplicationsListItem
                    serviceApplication={item}
                    onChange={handleRefresh}
                    identity={identity}
                  />
                </Badge.Ribbon>
              ) : (
                <ServiceApplicationsListItem
                  serviceApplication={item}
                  onChange={handleRefresh}
                />
              )}
            </List.Item>
          )}
          //locale={{ emptyText: "No applications found. Either you are not eligible or no applications were defined in this Service."}}
        />

        <PageHeader2
          title="Managed Resources"
          subTitle="Resources are something you own for personal or official use: workspaces, databases, websites, etc."
          icon={<DatabaseOutlined />}
        />

        <List
          grid={{ gutter: 16, column: 1 }}
          loading={isLoading}
          dataSource={serviceApplications}
          rowKey={(item) => item.id}
          renderItem={(item) => (
            <List.Item>
              <ManagedResourceTypesList
                serviceApplication={item}
                identity={identity}
              />
            </List.Item>
          )}
        />
      </div>
    );
};

ServiceApplicationsList.propTypes = {
  getServiceApplicationsInServiceCollection: PropTypes.func.isRequired,
  getServiceCollectionSubscriptionStatusForIdentity: PropTypes.func.isRequired,
  serviceApplications: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  serviceCollectionEligibilityStatus: PropTypes.string,
  serviceCollection: PropTypes.object,
  getIdentity: PropTypes.func,
  identity: PropTypes.object,
};

export default connect(
  (state) => ({
    serviceCollection:
      state.serviceCollectionSubscription.data?.serviceCollection,
    serviceCollectionEligibilityStatus:
      state.serviceCollectionSubscription.data?.status,
    serviceApplications: state.serviceApplications.data.sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    ),
    isLoading: state.serviceApplications.loading,
    identity: state.identity,
  }),
  {
    getServiceApplicationsInServiceCollection,
    getServiceCollectionSubscriptionStatusForIdentity,
    getIdentity,
  },
)(ServiceApplicationsList);
